<template>
  <div class="smart-form">
    <!-- 字符串类型 - 渲染为文本域 -->
    <a-textarea
      v-if="isString||value === null"
      v-model="stringValue"
      :autoSize="{ minRows: 15 }"
      type="textarea"
      @change="onStringValueChange"
      placeholder="请输入文本内容" />
    <!-- 对象类型 - 渲染为表单 -->
    <a-form-model
      v-else-if="isObject"
      ref="form"
      :model="objectValue">
      <a-row>
        <a-col v-for="(value, key) in objectValue" :key="key" :span="12">
          <a-form-model-item
            :label="$t('business.label.' + key)"
            :label-col="{span:8}"
            :wrapper-col="{span:16}">
            <!-- 字符串类型字段 -->
            <vxe-input v-if="typeof value === 'string'|| typeof value === 'number'" v-model="objectValue[key]"/>
            <!-- 对象类型字段 - 渲染为只读输入框，点击弹出编辑弹窗 -->
            <vxe-input
              v-else-if="typeof value === 'object' || Array.isArray(value)"
              :value="formatObjectValue(value)"
              style="width: 100%; cursor: pointer; background-color: #f5f5f5;"
              @click="openJsonEditor(key, value)"/>
            <!-- 日期类型字段 - 渲染为日期选择器 -->
            <a-date-picker
              v-else-if="isDate(value)"
              v-model="objectValue[key]"
              style="width: 100%"
              valueFormat="YYYY-MM-DD" />
            <!-- 布尔类型字段 - 渲染为复选框 -->
            <a-checkbox
              v-else-if="typeof value === 'boolean'"
              v-model="objectValue[key]" />
          </a-form-model-item>
        </a-col>
      </a-row>
      <!-- 添加JSON编辑弹窗 -->
      <a-modal
        v-model="jsonEditor.visible"
        title="编辑JSON"
        width="50%"
        :maskClosable="false"
        @ok="saveJsonEdit">
        <a-textarea
          v-model="jsonEditor.editingValue"
          :autoSize="{ minRows: 10 }"
          placeholder="请输入JSON内容" />
      </a-modal>
    </a-form-model>
    <!-- 数组类型 - 渲染为表格 -->
    <vxe-table
      v-else-if="isArray"
      :data="arrayValue"
      @change="onArrayValueChange">
      <vxe-column
        v-for="(_, key) in arrayColumns"
        :key="key"
        :field="key"
        :title="key">
        <template #default="{ row }">
          <!-- 字符串类型字段 -->
          <vxe-input v-if="typeof row[key] === 'string'" v-model="row[key]"/>
          <!-- 对象类型字段 - 渲染为下拉框 -->
          <vxe-select
            v-else-if="typeof row[key] === 'object' && !Array.isArray(row[key])"
            v-model="row[key]"
            style="width: 100%">
            <vxe-option
              v-for="(optionValue, optionKey) in row[key]"
              :key="optionKey"
              :value="optionKey">
              {{ optionValue }}
            </vxe-option>
          </vxe-select>
          <!-- 日期类型字段 - 渲染为日期选择器 -->
          <a-date-picker
            v-else-if="isDate(row[key])"
            v-model="row[key]"
            size="small"
            style="width: 100%"
            valueFormat="YYYY-MM-DD" />
          <!-- 布尔类型字段 - 渲染为复选框 -->
          <a-checkbox v-else-if="typeof row[key] === 'boolean'" v-model="row[key]" />
        </template>
      </vxe-column>
    </vxe-table>
  </div>
</template>
<script>
export default {
  name: 'SmartForm',
  props: {
    value: {
      type: [String, Object, Array],
      required: true,
      default: ''
    }
  },
  data() {
    return {
      stringValue: '',
      objectValue: {},
      arrayValue: [],
      arrayColumns: {},
      jsonEditor: {
        visible: false,
        currentKey: '',
        currentValue: '',
        editingValue: ''
      }
    };
  },
  computed: {
    isString() {
      const val = this.parseValue(this.value);
      return typeof val === 'string';
    },
    isObject() {
      const val = this.parseValue(this.value);
      return typeof val === 'object' && !Array.isArray(val);
    },
    isArray() {
      const val = this.parseValue(this.value);
      return Array.isArray(val);
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        const val = this.parseValue(newVal);
        if (this.isString) {
          this.stringValue = val;
        } else if (this.isObject) {
          this.objectValue = { ...val };
        } else if (this.isArray && val.length > 0) {
          this.arrayValue = [...val];
          // 提取第一个元素的key作为列
          this.arrayColumns = { ...val[0] };
        }
      }
    },
    objectValue: {
      deep: true,
      handler(newVal) {
        this.onObjectValueChange();
      }
    },
    arrayValue: {
      deep: true,
      handler(newVal) {
        this.onArrayValueChange();
      }
    }
  },
  methods: {
    formatObjectValue(value) {
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[Object]';
      }
    },
    openJsonEditor(key, value) {
      this.jsonEditor = {
        visible: true,
        currentKey: key,
        currentValue: value,
        editingValue: this.formatObjectValue(value)
      };
    },
    saveJsonEdit() {
      try {
        const parsed = JSON.parse(this.jsonEditor.editingValue);
        this.objectValue[this.jsonEditor.currentKey] = parsed;
        this.jsonEditor.visible = false;
      } catch (e) {
        this.$message.error('JSON格式错误');
      }
    },
    isDate(value) {
      if (typeof value !== 'string') return false;

      // 检查常见日期格式
      const datePatterns = [
        /\d{4}-\d{2}-\d{2}/,  // YYYY-MM-DD
        /\d{2}\/\d{2}\/\d{4}/, // DD/MM/YYYY
        /\d{4}\/\d{2}\/\d{2}/  // YYYY/MM/DD
      ];

      // 检查是否匹配任一日期格式
      const isDateLike = datePatterns.some(pattern => pattern.test(value));

      // 检查是否只包含日期相关字符
      const isCleanDate = /^[\d\-/]+$/.test(value);

      return isDateLike && isCleanDate;
    },
    getValue() {
      if (this.isString) {
        return this.stringValue;
      } else if (this.isObject) {
        return { ...this.objectValue };
      } else if (this.isArray) {
        return [...this.arrayValue];
      }
      return null;
    },
    parseValue(val) {
      if (typeof val === 'string') {
        try {
          const parsed = JSON.parse(val);
          if (parsed && typeof parsed === 'object') {
            return parsed;
          }
        } catch (e) {
          // 解析失败保持原样
        }
      }
      return val;
    },
    validate() {
      if (this.isObject && this.$refs.form) {
        return new Promise((resolve) => {
          this.$refs.form.validate(valid => {
            resolve(valid ? this.getValue() : null);
          });
        });
      }
      return Promise.resolve(this.getValue());
    },
    onStringValueChange() {
      console.log('StringValue changed:', this.stringValue);
      this.$emit('input', this.stringValue);
    },
    onObjectValueChange() {
      this.$emit('input', JSON.stringify(this.objectValue));
    },
    onArrayValueChange() {
      this.$emit('input', JSON.stringify(this.objectValue));
    }
  }
};
</script>

<style scoped>
.smart-form {
  width: 100%;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
